import { useCallback, useEffect, useMemo, useState } from 'react';
import { AxiosResponse } from 'axios';
import debounce from 'lodash/debounce';

import {
  appointLocationManagersRequest,
  deleteLocationManagersRequest,
  getLocationManagersRequest,
} from '@vyce/core/src/api/time';
import { GetStaffResponse, ListStaffRequest } from '@vyce/core/src/api/types';

type Props = {
  selectedCompanyId: string;
  isNewLocation?: boolean;
  locationId: string;
  getStaffRequest: (
    companyId: string,
    payload: ListStaffRequest
  ) => Promise<AxiosResponse<GetStaffResponse>>;
};

export const useAddNewSiteManager = ({
  selectedCompanyId,
  locationId,
  isNewLocation = false,
  getStaffRequest,
}: Props) => {
  const [staff, setStaff] = useState<{ uuid: string; name: string }[]>([]);
  const [staffSubstring, setStaffSubstring] = useState('');
  const [locationManagers, setLocationManagers] = useState<{ uuid: string; name: string }[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const addManager = async (id: string, newLocationId: string) => {
    try {
      await appointLocationManagersRequest({
        companyId: selectedCompanyId,
        siteId: locationId || newLocationId,
        userIds: [id],
      });
    } catch (e) {
      console.error(e);
    }
  };

  const deleteManager = async (id: string, newLocationId: string) => {
    try {
      await deleteLocationManagersRequest({
        companyId: selectedCompanyId,
        siteId: locationId || newLocationId,
        userIds: [id],
      });
    } catch (e) {
      console.error(e);
    }
  };

  const syncManagers = async (
    currentSetOfLocationManagers: { uuid: string; name: string }[],
    newLocationId?: string
  ) => {
    if (!locationId && !newLocationId) return;

    const initialSet = new Set(locationManagers?.map(item => item.uuid));
    const currentSet = new Set(currentSetOfLocationManagers?.map(item => item.uuid));

    const toAdd = Array.from(currentSet || []).filter(x => !initialSet.has(x));
    const toRemove = Array.from(initialSet || []).filter(x => !currentSet.has(x));

    const addPromises = toAdd?.map(id => addManager(id, newLocationId || ''));
    const removePromises = toRemove?.map(id => deleteManager(id, newLocationId || ''));

    await Promise.all([...addPromises, ...removePromises]);

    return newLocationId ? undefined : fetchLocationManagers();
  };

  const fetchStaff = useCallback(
    async (substring: string) => {
      if (!selectedCompanyId) return;
      try {
        setLoading(true);
        const { data } = await getStaffRequest(selectedCompanyId, {
          limit: 1000,
          offset: 0,
          substring,
          roles: ['Time&Attendance:Manager'],
        });
        setStaff(
          data.items.map(item => ({
            uuid: item.user_id,
            name: `${item.first_name} ${item.last_name}`,
          }))
        );
        setLoading(false);
      } catch (e) {
        setLoading(false);
      }
    },
    [selectedCompanyId]
  );

  const fetchLocationManagers = useCallback(async () => {
    if (!selectedCompanyId || !locationId) return;
    try {
      const { data } = await getLocationManagersRequest({
        companyId: selectedCompanyId,
        siteId: locationId,
        payload: {
          order_by: [
            {
              field: 'first_name',
              desc: true,
            },
          ],
        },
      });

      setLocationManagers(
        data.items.map(item => ({
          uuid: item.user_id,
          name: `${item.first_name} ${item.last_name}`,
        }))
      );
    } catch (e) {
      console.error(e);
    }
  }, [selectedCompanyId, locationId]);

  const debouncedFetchTags = useMemo(() => debounce(fetchStaff, 500), [fetchStaff]);

  useEffect(() => {
    debouncedFetchTags(staffSubstring);
  }, [staffSubstring, debouncedFetchTags]);

  useEffect(() => {
    if (isNewLocation) return;

    fetchLocationManagers();
  }, [fetchLocationManagers, isNewLocation]);

  return {
    loading,
    staff,
    locationManagers,
    syncManagers,
    setStaffSubstring,
  };
};
